import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LanguageLoader } from "../../assets/languages";
import Select from "react-select";
import { ReactSelectOptions } from "../utils";

const Dropdown = ({defaultValue, multiple, options, onChange, disabled}) =>
{
	const settings = useSelector((state) => state.settings);
	const preferences = useSelector((state) => state.preferences);
	const language = LanguageLoader(preferences.language);
	options = ReactSelectOptions(options);
	// Initialize with defaultValue if provided, otherwise use first option (which might be "0")
	const [value, SetValue] = useState(() => {
		if (multiple) return [];
		if (defaultValue !== undefined && defaultValue !== null) {
			return defaultValue;
		}
		return options?.[0]?.value ?? "";
	});
	const [firstLoad, SetFirstLoad] = useState(true);

	useEffect(() =>
	{
		// Check if defaultValue exists (handles arrays, strings, and numbers including 0)
		// For numbers, 0 is a valid value, so we need to check explicitly
		const hasDefaultValue = defaultValue !== undefined && defaultValue !== null && 
			(Array.isArray(defaultValue) ? defaultValue.length > 0 : 
			 (typeof defaultValue === 'string' ? defaultValue.length > 0 : 
			  (typeof defaultValue === 'number' ? true : defaultValue !== '')));
		
		if(hasDefaultValue)
		{
			// Normalize defaultValue to string to match option value types (options use string keys)
			// This ensures proper matching in the Select component
			const normalizedValue = String(defaultValue);
			
			// Update value when defaultValue is provided (handles both initial load and data loading in edit mode)
			if(firstLoad || String(value) !== normalizedValue)
			{
				SetValue(normalizedValue);
				// On first load only, call onChange to sync with parent
				// If defaultValue is "0" (blank option), pass undefined to onChange
				// This ensures existing records with "0" are treated as blank
				if(firstLoad)
				{
					const valueToPass = (defaultValue === "0" || defaultValue === 0) ? undefined : defaultValue;
					if(onChange)
					{
						onChange(valueToPass);
					}
					SetFirstLoad(false);
				}
			}
		}
		else if(firstLoad)
		{
			// If no defaultValue on first load, don't call onChange with initial "0" value
			// This prevents sending "0" for new records when user hasn't selected anything
			SetFirstLoad(false);
		}
	// eslint-disable-next-line
	}, [defaultValue]);
	
	const HandleChange = (dropdownValue) =>
	{
		// Handle null/undefined case
		if(!dropdownValue)
		{
			return;
		}
		
		let finalValue;
		let displayValue; // Value to store in state for display
		if(multiple)
		{
			finalValue = [];
			displayValue = [];
			for(const item of dropdownValue)
			{
				// IMPORTANT: Use item.value (English key), not item.label (Persian text)
				// Filter out "0" values (blank options) from multi-select
				const itemValue = item && item.value !== undefined ? item.value : item;
				if(itemValue !== "0" && itemValue !== 0)
				{
					finalValue.push(itemValue);
					displayValue.push(itemValue);
				}
			}
			// Return undefined if array is empty after filtering
			finalValue = finalValue.length > 0 ? finalValue : undefined;
			displayValue = displayValue.length > 0 ? displayValue : [];
		}
		else
		{
			// IMPORTANT: react-select returns {value: key, label: text}
			// We MUST use .value property to get the English key (e.g., "0", "1", "Other")
			// NOT .label which contains the Persian text (e.g., "سایر")
			const selectedValue = dropdownValue && dropdownValue.value !== undefined 
				? dropdownValue.value 
				: (dropdownValue || null);
			
			// When the value is "0" (blank option), return undefined instead of "0" or ""
			// This ensures blank selections send nothing instead of "0"
			// But keep "0" in state for display purposes
			displayValue = selectedValue;
			finalValue = (selectedValue === "0" || selectedValue === 0) ? undefined : selectedValue;
		}
		SetValue(displayValue);
		if(onChange)
		{
			onChange(finalValue);
		}
	}
	return <Select
		value={(multiple && value.length) ? options.filter((option) => value.includes(option.value)) : options.find((option) => option.value === value)}
		onChange={(selectedOption) => HandleChange(selectedOption)}
		placeholder={language.COMPONENTS.SELECT_OPTION_PLACEHOLDER}
		options={options}
		defaultValue={!multiple ? options[0] : null}
		isClearable={false}
		menuPlacement="auto"
		className={settings.strings.dropdown_container}
		classNamePrefix={settings.strings.dropdown_prefix}
		isDisabled={disabled}
		isMulti={multiple}
	/>
}

export default Dropdown;